import { FC, memo, MouseEvent, useEffect, useState } from "react";
import styles from "./UsersTable.module.scss";
import {
  IUser,
  UserKeysEnum,
  UserSortValuesEnum,
} from "@/entities/users/types";
import { Button, ModalWindow, PATHNAMES, Title, UserAvatar } from "@/shared";
import { useAppDispatch, useAppSelector } from "@/shared/hooks";
import { AddUserForm, SearchBar, SortArrow } from "@/features";
import {
  blockUser,
  fetchUsers,
  searchUser,
  setSortRules,
  setUserForReview,
  showUserDetails,
  sortUserList,
  unblockUser,
} from "@/entities/users/slice";
import EditPenIcon from "@/shared/assets/editPenIcon.svg";
import { UserRolesEnum } from "@/app/model/types";
import PlusIcon from "@/shared/assets/plusIcon.svg";
import CrossIcon from "@/shared/assets/crossIcon.svg";

import {
  fetchOneDealer,
  searchDealerUser,
  sortDealerUserList,
} from "@/entities/dealer";
import { useLocation, useNavigate } from "react-router-dom";
import { useDrawer } from "@/app/providers/DrawerProvider/useDrawer";
import { EditUserForm } from "@/features/forms/editUserForm/EditUserForm";
import { Switch, Tooltip } from "antd";
import LockIcon from "@/shared/assets/lockIcon.svg";
import RedLockIcon from "@/shared/assets/closedLockIcon.svg";
import clsx from "clsx";
import { UserInfo } from "@/widgets/userInfo/UserInfo";
import LoginIcon from "@/shared/assets/login.svg";
import { loginAs } from "@/app/model/applicationSlice";
import { getUser } from "@/entities/viewer/slice";
import $api from "@/shared/http";

export interface UsersTableProps {
  usersList: IUser[];
  isDealerView?: boolean;
  isTechAdmin?: boolean;
}
const { nomenclaturePath } = PATHNAMES;
export const UsersTable: FC<UsersTableProps> = memo(function UsersTable({
  usersList,
  isDealerView = false,
  isTechAdmin = false,
}) {
  const { hideUserColumnsList, sortRules, searchCode, userForReview } =
    useAppSelector((state) => state.users);
  const {
    roleName,
    dealerId,
    isSkadUser,
    id: ownId,
  } = useAppSelector((state) => state.viewer);
  const { currentDealer } = useAppSelector((state) => state.dealers);
  const { isAuth, isLoading } = useAppSelector((state) => state.app);
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();

  const [showModal, setshowModal] = useState(false);
  const [textareaValue, setTextareaValue] = useState("");
  const [onlyBlocked, setOnlyBlocked] = useState(false);
  const [filteredList, setFilteredList] = useState(usersList);
  const [searchValue, setSearchValue] = useState("");

  const { openDrawer } = useDrawer();
  const navigate = useNavigate();

  useEffect(() => {
    searchByCode("");
  }, [pathname]);

  useEffect(() => {
    if (onlyBlocked) {
      setFilteredList(usersList.filter((u) => u.blockedDate));
    } else {
      setFilteredList(usersList);
    }
  }, [usersList]);

  useEffect(() => {
    if (isTechAdmin && !isLoading && isAuth) {
      fetchUser();
      dispatch(getUser());
    }
  }, [isLoading, isAuth, isTechAdmin]);

  const tableHeaders = Object.keys(UserKeysEnum) as Array<
    keyof typeof UserKeysEnum
  >;

  const sortValues = Object.values(UserSortValuesEnum);

  const excludeTableHeaders: (keyof typeof UserKeysEnum)[] = [
    "id",
    "image",
    "blockedDate",
    "registrationDate",
    "dealerId",
    "dealerName",
    "managerDealers",
  ] as const;

  const showBlockUser = ["Admin", "Dealer", "TechAdmin"].includes(roleName);

  const canCreateUser =
    roleName === "Admin" ||
    roleName === "TechAdmin" ||
    (roleName === "Manager" && currentDealer) ||
    (roleName === "Dealer" && currentDealer);

  const handleSort = (value: { name: string; type: "asc" | "desc" }) => {
    const newSortObj: typeof sortRules = {
      field: value.name as keyof typeof UserSortValuesEnum,
      type: value.type,
    };

    dispatch(setSortRules(newSortObj));
    !isDealerView
      ? dispatch(sortUserList(newSortObj))
      : dispatch(sortDealerUserList(newSortObj));
  };

  const handleOpenUserReview = (id: string) => {
    const user = usersList.find((u) => u.id === id);
    isDealerView
      ? dispatch(setUserForReview(user))
      : dispatch(showUserDetails(id));
    openDrawer(<UserInfo />, { maxWidth: 475 });
  };

  const handleOpenUserEdit = (id: string) => {
    const user = usersList.find((u) => u.id === id);
    isDealerView
      ? dispatch(setUserForReview(user))
      : dispatch(showUserDetails(id));
    openDrawer(
      <>
        <Title text="Редактировать данные" type="default" />
        {user && (
          <EditUserForm
            currentUser={user}
            viewType={currentDealer ? "dealer" : "skad"}
          />
        )}
      </>,
      {},
    );
  };

  const blockUserHandler = (
    e: MouseEvent<HTMLOrSVGElement>,
    user: IUser,
  ): void => {
    e.stopPropagation();
    dispatch(setUserForReview(user));
    setshowModal(true);
  };

  const searchByCode = (value: string) => {
    setSearchValue(value);
    !isDealerView
      ? dispatch(searchUser(value))
      : dispatch(searchDealerUser(value));
  };

  const handleAddNewUserClick = () => {
    openDrawer(
      <>
        <Title text="Создать нового пользователя" type="default" />
        <AddUserForm
          dealerId={(!isSkadUser && dealerId) || currentDealer?.id}
          dealerName={currentDealer?.name || (!isSkadUser && dealerId)}
        />
      </>,
      {},
    );
  };

  const switcherChangeHandler = (checked: boolean) => {
    setOnlyBlocked(checked);
    if (checked) {
      setFilteredList((prev) => prev.filter((u) => u.blockedDate));
    } else {
      setFilteredList(usersList);
    }
  };

  const handleClickSubmit = async () => {
    userForReview.blockedDate
      ? await dispatch(
          unblockUser({
            id: userForReview.id,
            comment: textareaValue,
          }),
        )
      : await dispatch(
          blockUser({
            id: userForReview.id,
            comment: textareaValue,
          }),
        );
    await dispatch(fetchUsers(""));
    if ((isSkadUser || isTechAdmin) && currentDealer) {
      await dispatch(fetchOneDealer(userForReview.dealerId));
    }
    setshowModal(false);
    setTextareaValue("");
  };

  const handleClickCancel = () => {
    setTextareaValue("");
    setshowModal(false);
  };

  const handleLogin = (e: MouseEvent<HTMLOrSVGElement>, user: IUser) => {
    e.stopPropagation();
    if (user.blockedDate) return;
    dispatch(loginAs({ targetUserId: user.id }));
  };

  const fetchUser = async () => {
    const response = await $api.get("/user/info");
    if (response.data.data.roleName !== "TechAdmin") navigate(nomenclaturePath);
  };

  return (
    <>
      <div className={styles.contentBar}>
        <SearchBar
          placeholder="Поиск пользователя"
          searchFunction={searchByCode}
          doOnChange={searchByCode}
          defaultValue={searchValue}
          rightSlot={
            !!searchValue && (
              <CrossIcon
                className={styles.clearIcon}
                width={12}
                height={12}
                onClick={() => searchByCode("")}
              />
            )
          }
        />
        {canCreateUser && (
          <div className={styles.actionButtons}>
            <Button
              onClick={handleAddNewUserClick}
              name="Новый пользователь"
              buttontStyle={"noBG"}
            >
              <PlusIcon />
            </Button>
          </div>
        )}
      </div>
      {showBlockUser && (
        <div className={styles.switcherBar}>
          <span>Только заблокированные</span>
          <Switch
            value={onlyBlocked}
            size="small"
            onChange={switcherChangeHandler}
          />
        </div>
      )}
      <div className={styles.mainTableBlock}>
        {usersList && usersList.length > 0 ? (
          <>
            <div className={styles.leftTableBlock}>
              <table className={styles.tableMain}>
                <thead>
                  <tr className={styles.tableHeaders}>
                    {tableHeaders && tableHeaders ? (
                      tableHeaders
                        .filter(
                          (item) =>
                            !hideUserColumnsList.includes(item) &&
                            !excludeTableHeaders.includes(item),
                        )
                        .map((item) => (
                          <th key={item + item + Math.random() * 399}>
                            <div className={styles.thdiv}>
                              {UserKeysEnum[item]}
                              {!searchCode && sortValues.includes(item) && (
                                <SortArrow
                                  sortFunction={handleSort}
                                  name={item}
                                  selectedName={sortRules.field}
                                  selectedType={sortRules.type}
                                />
                              )}
                            </div>
                          </th>
                        ))
                    ) : (
                      <></>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredList.map((user: IUser, index) => {
                    return (
                      <tr
                        key={user.id + index}
                        onClick={() => {
                          handleOpenUserReview(user.id);
                        }}
                      >
                        {tableHeaders.map(
                          (userValue: keyof typeof UserKeysEnum) => {
                            if (
                              !hideUserColumnsList.includes(userValue) &&
                              !excludeTableHeaders.includes(userValue)
                            ) {
                              return (
                                <td key={userValue}>
                                  <div className={styles.tddiv}>
                                    {userValue === "fio" && (
                                      <div className={styles.userPhotoBlock}>
                                        <UserAvatar
                                          fio={
                                            user[userValue] as keyof typeof user
                                          }
                                          image={user.image}
                                        />
                                      </div>
                                    )}
                                    {userValue === "roleName"
                                      ? UserRolesEnum[user[userValue]]
                                      : (user[userValue] as keyof typeof user)}
                                  </div>
                                </td>
                              );
                            }
                          },
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {roleName !== "Sales" && roleName !== "Marketer" && (
              <div className={styles.rightTableBlock}>
                <table className={styles.tableRight}>
                  <thead>
                    <tr className={styles.tableHeaders}>
                      <th className={styles.lastColumn}>
                        <div> </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersList &&
                      filteredList.map((user: IUser) => {
                        const canBlock = showBlockUser && user.id !== ownId;

                        return (
                          <tr key={user.id.toString() + Math.random() * 99}>
                            <td className={styles.lastColumn}>
                              <div>
                                {isTechAdmin &&
                                  user.roleName !== "ApiDealer" && (
                                    <div>
                                      <Tooltip
                                        title={
                                          user.blockedDate
                                            ? "Вход невозможен. Пользователь заблокирован"
                                            : "Авторизоваться под данным пользователем"
                                        }
                                        overlayClassName={styles.tooltip}
                                      >
                                        <LoginIcon
                                          className={clsx(
                                            styles.login,
                                            user.blockedDate &&
                                              styles.disabledLogin,
                                          )}
                                          onClick={(
                                            e: MouseEvent<HTMLOrSVGElement>,
                                          ) => handleLogin(e, user)}
                                        />
                                      </Tooltip>
                                    </div>
                                  )}
                                <>
                                  {canBlock && (
                                    <div
                                      className={clsx(styles.editIcon, {
                                        [styles.redIcon]: user.blockedDate,
                                      })}
                                      onClick={() => {
                                        handleOpenUserEdit(user?.id);
                                      }}
                                    >
                                      <Tooltip
                                        title={
                                          user.blockedDate
                                            ? "Заблокирован"
                                            : "Активный"
                                        }
                                      >
                                        {user.blockedDate ? (
                                          <RedLockIcon
                                            id="blockedUser"
                                            onClick={(
                                              e: MouseEvent<HTMLOrSVGElement>,
                                            ) => blockUserHandler(e, user)}
                                          />
                                        ) : (
                                          <LockIcon
                                            id="activeUser"
                                            onClick={(
                                              e: MouseEvent<HTMLOrSVGElement>,
                                            ) => blockUserHandler(e, user)}
                                          />
                                        )}
                                      </Tooltip>
                                    </div>
                                  )}
                                  {
                                    <div
                                      className={styles.editIcon}
                                      onClick={() => {
                                        handleOpenUserEdit(user?.id);
                                      }}
                                    >
                                      <EditPenIcon />
                                    </div>
                                  }
                                </>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <div className={styles.empty}>
            <p>Пользователи не найдены</p>
            {canCreateUser && (
              <div>
                <Button
                  name="Добавить пользователя"
                  buttontStyle={"default"}
                  onClick={handleAddNewUserClick}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {showModal && userForReview && (
        <ModalWindow type="info" cancelButtonText="Нет" buttonsPosition="right">
          <div className={styles.blockModalText}>
            <>
              <p>{`Вы уверены, что хотите ${
                userForReview.blockedDate ? "разблокировать" : "заблокировать"
              }`}</p>
              <p>
                <strong>{userForReview.fio}</strong>?
              </p>
              <br />

              <p className={styles.smallText}>
                Доступ <strong>{userForReview.fio} </strong>
                {`к системе будет
                  ${
                    userForReview.blockedDate
                      ? "полностью восстановлен"
                      : " закрыт"
                  }!`}
              </p>

              <textarea
                placeholder={`Причина ${
                  userForReview.blockedDate ? "разблокировки" : "блокировки"
                } (обязательно)`}
                value={textareaValue}
                onChange={(e) => setTextareaValue(e.target.value)}
              />
              <div className={styles.buttons}>
                <Button
                  buttontStyle="cancelBtn"
                  name="Нет"
                  onClick={handleClickCancel}
                />
                <Button
                  buttontStyle="default"
                  name="Да"
                  disabled={!textareaValue}
                  onClick={handleClickSubmit}
                />
              </div>
            </>
          </div>
        </ModalWindow>
      )}
    </>
  );
});
